import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Input, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { Card, Column } from '@cards/Card';
import HeaderButton from '@components/HeaderButton';
import { usePageActions, useSearch } from '@hooks';
import { loadDirectChildOrganisations } from '@redux/directChildOrganisationAdministration/actions';
import {
  selectDirectChildOrganisations,
  selectDirectChildOrganisationsState,
} from '@redux/directChildOrganisationAdministration/selectors';
import { setCurrentScreen, screens } from '../../../analytics';
import '../directChildOrganisationAdministration.less';

const matchesName = (organisation, comparator) => comparator(organisation.name);

const DirectChildOrganisationList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const { t } = useTranslation();
  const organisations = useSelector(selectDirectChildOrganisations);
  const collection = useSelector(selectDirectChildOrganisationsState);
  const [searchResults, searchProps] = useSearch(organisations, matchesName);
  const title = t('directChildOrganisationAdministration:organisations.list.title');

  const { hasPageActionsProvider } = usePageActions({
    title,
    actions: [
      <HeaderButton
        key="new-direct-child-organisation"
        tooltip={t('directChildOrganisationAdministration:organisations.list.new')}
        onClick={() => history.push(`${match.url}/new`)}
      >
        {t('directChildOrganisationAdministration:organisations.list.new')}
      </HeaderButton>,
    ],
  });

  useEffect(() => {
    setCurrentScreen(screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATIONS);
  }, []);

  useEffect(() => {
    dispatch(loadDirectChildOrganisations());
  }, [dispatch]);

  const columns = [
    {
      title: t('directChildOrganisationAdministration:organisations.list.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
  ];
  const listErrorKeyByStatus = {
    403: 'forbidden',
    404: 'notFound',
    502: 'upstream',
  };
  const listErrorKey =
    listErrorKeyByStatus[collection.errorStatus] ||
    {
      DIRECT_CHILD_ORGANISATION_FORBIDDEN: 'forbidden',
      ORGANISATION_NOT_FOUND: 'notFound',
      UPSTREAM_SERVICE_UNAVAILABLE: 'upstream',
    }[collection.errorCode] ||
    'failure';

  return (
    <Column>
      {!hasPageActionsProvider ? <h1>{title}</h1> : null}
      <Card.Full className="direct-child-organisation-list-card" noPadding>
        <div className="search-container">
          <Input.Search
            aria-label={t('directChildOrganisationAdministration:organisations.list.search')}
            placeholder={t('directChildOrganisationAdministration:organisations.list.search')}
            {...searchProps}
          />
          {!hasPageActionsProvider ? (
            <Button type="primary" onClick={() => history.push(`${match.url}/new`)}>
              {t('directChildOrganisationAdministration:organisations.list.new')}
            </Button>
          ) : null}
        </div>
        {collection.status === 'failure' ? (
          <section role="alert">
            <p>{t(`directChildOrganisationAdministration:organisations.list.${listErrorKey}`)}</p>
            <Button onClick={() => dispatch(loadDirectChildOrganisations())}>
              {t('directChildOrganisationAdministration:organisations.list.retry')}
            </Button>
          </section>
        ) : (
          <Table
            columns={columns}
            dataSource={searchResults}
            loading={collection.status === 'loading'}
            locale={{
              emptyText:
                collection.status === 'success'
                  ? t('directChildOrganisationAdministration:organisations.list.empty')
                  : null,
            }}
            pagination={{ position: 'bottom' }}
            rowKey="slug"
            scroll={{ x: 1000 }}
            onRow={organisation => ({
              onClick: () => history.push(`${match.url}/${organisation.slug}/setup`),
            })}
          />
        )}
      </Card.Full>
    </Column>
  );
};

export default DirectChildOrganisationList;
